import { useRef, type KeyboardEvent } from 'react';

export type TabItem = {
  key: string;
  label: string;
  // Optional count badge (e.g. outstanding items); rendered only when > 0.
  badge?: number;
};

// Tab strip with an underlined active tab and optional count badges. Tabs wrap
// onto additional lines when they don't fit, rather than scrolling horizontally.
// Controlled: the parent owns the active key and handles selection. Implements
// the WAI-ARIA tabs keyboard pattern (Left/Right/Home/End + roving tabindex);
// pair with a panel that has role="tabpanel" id={`tabpanel-${key}`}.
export function Tabs({
  tabs,
  active,
  onSelect,
  ariaLabel,
  className = '',
}: {
  tabs: TabItem[];
  active: string;
  onSelect: (key: string) => void;
  ariaLabel?: string;
  // Extra classes for the tab strip container (e.g. padding to align the strip
  // inside a card). Horizontal padding here keeps the full-width bottom border
  // because the container stays block-level full-width.
  className?: string;
}) {
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const keys = tabs.map((t) => t.key);
    const i = keys.indexOf(active);
    if (i < 0) return;
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % keys.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = keys.length - 1;
    else return;
    e.preventDefault();
    const nextKey = keys[next]!;
    onSelect(nextKey);
    btnRefs.current[nextKey]?.focus();
  }

  return (
    <div
      className={`flex flex-wrap gap-x-1 gap-y-1 border-b border-slate-200 ${className}`}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            ref={(el) => {
              btnRefs.current[t.key] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${t.key}`}
            aria-controls={`tabpanel-${t.key}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(t.key)}
            className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm transition ${
              isActive
                ? 'border-blue-600 font-semibold text-blue-600'
                : 'border-transparent font-medium text-slate-600 hover:text-slate-800'
            }`}
          >
            {t.label}
            {t.badge !== undefined && t.badge > 0 && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
                  isActive ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
